import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Grid, Stack, Typography, Button, Link, Box, Skeleton} from 'views/components';
import {Translate, translate, MarkdownViewer, ShortDateText} from 'views/module';

import backend from 'utils/backend';
import moment from 'moment-timezone';

const ViewEvent = (props) => {
  const [clientData, setClientData] = React.useState(null);

  const onClose = props.onClose;
  const event = props.event;

  const startTimeMoment = moment.tz(event.eventData.startTime, event.eventData.timezone);
  const endTimeMoment = moment.tz(event.eventData.endTime, event.eventData.timezone);

  // Load client data
  React.useEffect(() => {
    backend.client.getClient(event.clientRn, true)
        .then((e) => {
          setClientData(e);
        })
        .catch(() => {
          console.error('Failed to load client data');
        });
  }, [event]);

  return (
    <Dialog onClose={onClose} open={true}>
      <DialogTitle onClose={onClose}>
        <Translate expression='caregiver.CaregiverDetail.ChangeStatusDialog.Title' />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1} >
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.EventInfo.Title'/>
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
              }}
            >
              <Link
                href={`/schedule/event/${props.eventRn}`}
              >
                {event.info.title}
              </Link>
            </Typography>
          </Stack>
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.EventInfo.Description'/>
            </Typography>
            <Box
              sx={{
                fontSize: '14px',
              }}
            >
              {
                !event.info.description.blob &&
                translate('caregiver.CaregiverDetail.Scheduling.ScheduleDetail.EventInfo.NoDescription')
              }
              {
                event.info.description.blob &&
                <MarkdownViewer
                  value={event.info.description.blob}
                  truncate={true}
                />
              }
            </Box>
          </Stack>
          <Stack>
            <Grid container>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.StartTime'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    <ShortDateText dateTimestamp={startTimeMoment}/>
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.EndTime'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    <ShortDateText dateTimestamp={endTimeMoment}/>
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.Timezone'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {event.eventData.timezone}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          {
            event.breaks && (
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.Breaks.Title'/>
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.Breaks.BreakDuration'/>:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                      }}
                    >
                      <Translate
                        expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.Breaks.Minutes'
                        substitute={{breakDuration: event.breaks.breakDuration}}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.Breaks.BreakCount'/>:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                      }}
                    >
                      {event.breaks.breakCount}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            )
          }
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.Client.ClientName'/>
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
              }}
            >
              {clientData ? (
                <Link href={`/client/${clientData.clientRn}`}>
                  {clientData.clientDetail.clientName}
                </Link>
              ) : (
                <Skeleton sx={{fontSize: '14px', width: '100px'}} />
              )}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='caregiver.CaregiverDetail.Scheduling.ScheduleDetail.Action.Close' />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ViewEvent.propTypes = {
  onClose: PropTypes.func.isRequired,
  eventRn: PropTypes.string.isRequired,
  event: PropTypes.object.isRequired,
};

export default React.memo(ViewEvent);
